import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { isApiError, socialApi } from "@bw/shared/api";
import type { MessagePage, MessageResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import {
  MESSAGE_PAGE_SIZE,
  mergeHeadAuthority,
  pageAuthoritySignature,
  preserveCurrentHeadOnPageAppend,
  replaceLatestAuthority,
} from "./messageCacheAuthority";
import {
  messageReadAuthority,
  withMessagePageReadAuthority,
} from "./messageReadAuthority";
import { revokeConversation } from "../../../common/hooks/conversationCacheAuthority";
import {
  installRefreshedReadAuthority,
  messageProjectionSignature,
  readAuthorityCursor,
  recordReadAuthorityCursor,
  replaceReadAuthorityCursor,
} from "./messageAuthorityRecovery";

const MESSAGES_REFETCH_MS = 30_000;

function isTerminalAuthorityError(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.status === 401 ||
      error.status === 403 ||
      error.status === 404 ||
      error.status === 409)
  );
}

function flattenMessages(
  data: InfiniteData<MessagePage> | undefined,
): MessageResponse[] {
  const seen = new Set<string>();
  const messages: MessageResponse[] = [];
  for (const page of data?.pages ?? []) {
    for (const message of page.items) {
      if (!seen.has(message.message_id)) {
        seen.add(message.message_id);
        messages.push(message);
      }
    }
  }
  return messages;
}

function uniqueReadBatches(
  data: InfiniteData<MessagePage> | undefined,
): string[][] {
  return messageReadAuthority(data);
}

/**
 * Signed-keyset message history. Older pages accumulate without duplicates.
 * Each authority poll refreshes only the head, preserving loaded history while
 * keeping background work constant. Polls are lifecycle-bound and never
 * recreate a cache that an auth transition or terminal authority denial cleared.
 */
export function useMessages(
  conversationId: string | undefined,
  options: { history?: boolean; active?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const includeHistory = options.history ?? true;
  const [authorityFailure, setAuthorityFailure] = useState<{
    conversationId: string;
    error: unknown;
  } | null>(null);
  const currentAuthorityFailure =
    authorityFailure?.conversationId === conversationId
      ? authorityFailure
      : null;
  const queryKey = useMemo(
    () =>
      includeHistory
        ? queryKeys.social.messages(conversationId ?? "")
        : queryKeys.social.latestMessages(conversationId ?? ""),
    [conversationId, includeHistory],
  );
  const staleAuthorityControllerRef = useRef<AbortController | null>(null);
  const enabled =
    Boolean(conversationId) &&
    (options.active ?? true) &&
    !currentAuthorityFailure;
  // Start inactive so a latest-page consumer that mounts over a still-fresh
  // cache (close→reopen or mobile→desktop) performs one current read too.
  // A cold mount has no data, so its normal initial query remains the only read.
  const previousEnabledRef = useRef(false);
  const revokeAuthority = useCallback(
    async (error: unknown) => {
      if (!conversationId) return;
      await revokeConversation(queryClient, conversationId);
      setAuthorityFailure({ conversationId, error });
    },
    [conversationId, queryClient],
  );
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const cursor = typeof pageParam === "string" ? pageParam : null;
      let page: MessagePage;
      try {
        page = await socialApi.getMessages(conversationId as string, {
          limit: MESSAGE_PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
          signal,
        });
      } catch (error) {
        if (isTerminalAuthorityError(error)) await revokeAuthority(error);
        throw error;
      }
      if (!signal.aborted) {
        recordReadAuthorityCursor(
          queryClient,
          conversationId as string,
          page.read_message_ids,
          cursor,
        );
      }
      return withMessagePageReadAuthority(page);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page: MessagePage) =>
      includeHistory && page.has_more ? page.next_cursor : undefined,
    // A next-page request is built from the cache snapshot that existed when
    // it started. If a confirmed head poll lands before that request resolves,
    // preserve the newer cached head while appending the older page.
    structuralSharing: (current, incoming) =>
      preserveCurrentHeadOnPageAppend(
        current as InfiniteData<MessagePage> | undefined,
        incoming as InfiniteData<MessagePage>,
      ),
    // Latest-only consumers own freshness through the bounded manual poll and
    // the explicit inactive→active refresh below. Keeping cached latest data
    // fresh prevents TanStack from racing a second automatic restore request.
    staleTime: includeHistory ? undefined : Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });

  useEffect(() => {
    const wasEnabled = previousEnabledRef.current;
    previousEnabledRef.current = enabled;
    if (!wasEnabled && enabled && !includeHistory && query.data) {
      void query.refetch();
    }
  }, [enabled, includeHistory, query.data, query.refetch]);

  const refreshReadAuthorityBatch = useCallback(
    async (messageIds: string[]) => {
      if (!conversationId) throw new Error("Conversation is unavailable");
      const cursor = readAuthorityCursor(
        queryClient,
        conversationId,
        messageIds,
      );
      if (cursor === undefined) {
        throw new Error("Message read authority is unavailable");
      }
      const before =
        queryClient.getQueryData<InfiniteData<MessagePage>>(queryKey);
      if (!before) throw new Error("Message history is unavailable");
      const beforeProjection = messageProjectionSignature(before, messageIds);
      staleAuthorityControllerRef.current?.abort();
      const controller = new AbortController();
      staleAuthorityControllerRef.current = controller;
      let page: MessagePage;
      try {
        page = await socialApi.getMessages(conversationId, {
          limit: MESSAGE_PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
          signal: controller.signal,
        });
      } catch (error) {
        if (isTerminalAuthorityError(error)) await revokeAuthority(error);
        throw error;
      }
      if (controller.signal.aborted) {
        throw new DOMException("Authority refresh aborted", "AbortError");
      }
      const pageItemIds = new Set(
        page.items.map((message) => message.message_id),
      );
      if (
        page.read_message_ids.some((messageId) => !pageItemIds.has(messageId))
      ) {
        throw new Error("Message read authority is malformed");
      }
      let installed = false;
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        queryKey,
        (current) => {
          if (
            !current ||
            messageProjectionSignature(current, messageIds) !== beforeProjection
          ) {
            return current;
          }
          installed = true;
          return installRefreshedReadAuthority(
            current,
            messageIds,
            page,
            cursor,
          );
        },
      );
      if (installed) {
        replaceReadAuthorityCursor(
          queryClient,
          conversationId,
          messageIds,
          page.read_message_ids,
          cursor,
        );
      }
    },
    [conversationId, queryClient, queryKey, revokeAuthority],
  );

  useEffect(
    () => () => {
      staleAuthorityControllerRef.current?.abort();
    },
    [conversationId],
  );

  useEffect(() => {
    if (!enabled || !conversationId) return;

    let active = true;
    let running = false;
    let controller: AbortController | null = null;

    const pollAuthority = async () => {
      if (!active || running) return;
      const before =
        queryClient.getQueryData<InfiniteData<MessagePage>>(queryKey);
      if (!before?.pages[0]) return;
      running = true;
      controller = new AbortController();

      try {
        const beforeHeadSignature = pageAuthoritySignature(before.pages[0]);
        const head = await socialApi.getMessages(conversationId, {
          limit: MESSAGE_PAGE_SIZE,
          signal: controller.signal,
        });
        if (!active || controller.signal.aborted) return;
        recordReadAuthorityCursor(
          queryClient,
          conversationId,
          head.read_message_ids,
          null,
        );
        queryClient.setQueryData<InfiniteData<MessagePage>>(
          queryKey,
          (current) => {
            // An auth-boundary clear is authoritative. A late response must not
            // recreate the previous account's private message cache.
            if (
              !current ||
              pageAuthoritySignature(current.pages[0]) !== beforeHeadSignature
            ) {
              return current;
            }
            if (includeHistory) return mergeHeadAuthority(current, head);
            return replaceLatestAuthority(current, head);
          },
        );
      } catch (error) {
        if (isTerminalAuthorityError(error)) {
          // Membership revocation and either-way blocks revoke authority over
          // every cached projection of this conversation. Do not leave private
          // bodies visible while an ordinary retry would keep returning a
          // terminal denial.
          await revokeAuthority(error);
          return;
        }
        // Background authority failures retain the last confirmed cache. The
        // next bounded poll retries without exposing transport details.
      } finally {
        running = false;
      }
    };

    const interval = window.setInterval(() => {
      void pollAuthority();
    }, MESSAGES_REFETCH_MS);
    const refreshAuthority = () => {
      void pollAuthority();
    };
    window.addEventListener("focus", refreshAuthority);
    window.addEventListener("online", refreshAuthority);
    // Latest-page consumers get their one activation read from refetchOnMount.
    // History consumers retain the manual bounded-head activation path.
    if (includeHistory) void pollAuthority();
    return () => {
      active = false;
      controller?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshAuthority);
      window.removeEventListener("online", refreshAuthority);
    };
  }, [
    conversationId,
    enabled,
    includeHistory,
    queryClient,
    queryKey,
    revokeAuthority,
  ]);

  return {
    ...query,
    ...(currentAuthorityFailure
      ? {
          data: undefined,
          error: currentAuthorityFailure.error,
          isError: true,
          isLoading: false,
        }
      : {}),
    messages: currentAuthorityFailure ? [] : flattenMessages(query.data),
    readMessageBatches: currentAuthorityFailure
      ? []
      : uniqueReadBatches(query.data),
    refreshReadAuthorityBatch,
    revokeAuthority,
  };
}
